<script lang="ts">
import { CompanySearchResultsWithoutData, EventSearchResultsWithoutData, VisiblePage } from "@/Types/pagination";
import { defineComponent, PropType } from "vue";

export default defineComponent({
    components: {},
    props: {
        paginationData: {
            type: Object as PropType<CompanySearchResultsWithoutData | EventSearchResultsWithoutData>,
            required: true,
        },
    },
    emits: ["paginate"],
    data: () => ({}),
    computed: {
        currentPage(): number {
            return this.paginationData.current_page;
        },
        lastPage(): number {
            return this.paginationData.last_page;
        },

        isFirstPage(): boolean {
            return this.paginationData.current_page === 1;
        },
        isLastPage(): boolean {
            return this.paginationData.current_page === this.paginationData.last_page;
        },

        visiblePages(): VisiblePage[] {
            const pages: VisiblePage[] = [];
            const current = this.currentPage;
            const last = this.lastPage;

            const addPage = (page: number): void => {
                pages.push({
                    key: `page-${page}`,
                    label: String(page),
                    page,
                    isDots: false,
                });
            };

            const addDots = (key: string): void => {
                pages.push({
                    key,
                    label: "...",
                    page: -1,
                    isDots: true,
                });
            };

            // normal behaviour, when there are only so few pages that all can be viewed
            if (last <= 7) {
                for (let page = 1; page <= last; page++) {
                    addPage(page);
                }

                return pages;
            }

            // when there are more pages, some in the middle are replaced by "..."

            addPage(1);

            if (current > 3) {
                addDots("dots-before");
            }

            for (let page = Math.max(2, current - 1); page <= Math.min(last - 1, current + 1); page++) {
                addPage(page);
            }

            if (current < last - 2) {
                addDots("dots-after");
            }

            addPage(last);

            return pages;
        },
    },
    methods: {
        goToPage(page: number): void {
            if (page < 1) return;
            if (page > this.lastPage) return;
            if (page === this.currentPage) return;

            this.$emit("paginate", page);
        },
    },
});
</script>

<template>
    <div class="pagination-new flex flex--v flex--valign-c flex--gap-20">
        <nav v-if="paginationData.last_page > 1" class="pagination-new-nav flex flex--valign-c flex--gap-10">
            <button class="pagination-button" :disabled="isFirstPage" @click="goToPage(currentPage - 1)">
                <span class="pagination-button-text pagination-button-text--arrow text text--accent-gray-a text--size-40 text--bold">
                    {{ "<" }}
                </span>
            </button>

            <div class="pagination-buttons-inner flex flex--valign-c flex--gap-0">
                <button v-for="page in visiblePages" :key="page.key" class="pagination-button" :class="{ active: page.page === currentPage, dots: page.isDots }" :disabled="page.isDots" @click="goToPage(page.page)">
                    <span class="pagination-button-text text text--accent-gray-a text--size-22 text--medium">
                        {{ page.label }}
                    </span>
                </button>
            </div>

            <button class="pagination-button" :disabled="isLastPage" @click="goToPage(currentPage + 1)">
                <span class="pagination-button-text pagination-button-text--arrow text text--accent-gray-a text--size-40 text--bold">
                    {{ ">" }}
                </span>
            </button>
        </nav>

        <span class="pagination-count text text--size-18 text--primary-b text--medium text--center">
            {{ $trans("main.pagination-count", { args: { from: paginationData.from, to: paginationData.to, records: paginationData.total } }) }}
        </span>
    </div>
</template>

<style lang="scss" scoped></style>
